import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Home, Search, Briefcase, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSeoRedirect } from "@/hooks/useSeoRedirect";

const NotFound = () => {
  const location = useLocation();
  const redirectStatus = useSeoRedirect();

  useEffect(() => {
    if (redirectStatus !== "none") return;

    // Log 404 voor SEO analyse (best-effort, fail silent)
    const path = location.pathname + (location.search || "");
    (async () => {
      try {
        await supabase.rpc("log_404", {
          _path: path,
          _referrer: typeof document !== "undefined" ? document.referrer || null : null,
          _user_agent: typeof navigator !== "undefined" ? navigator.userAgent || null : null,
        });
      } catch {
        /* fail silent */
      }
    })();

    console.warn("404:", path);
  }, [location.pathname, location.search, redirectStatus]);

  // Tijdens redirect-check niets renderen om flicker te voorkomen
  if (redirectStatus === "checking" || redirectStatus === "redirected") {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Pagina niet gevonden (404) — ChillWerk.nl</title>
        <meta name="robots" content="noindex, follow" />
        <meta
          name="description"
          content="De pagina die je zoekt bestaat niet meer. Bekijk onze actuele vacatures of ga terug naar de homepage."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-7xl md:text-8xl font-heading font-black text-primary/20 mb-4">
              404
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Oeps, deze pagina bestaat niet (meer)
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              De link is misschien verouderd of de pagina is verplaatst. Geen zorgen, hier zijn een paar handige plekken om verder te kijken:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              <Button asChild size="lg" className="h-auto py-4">
                <Link to="/vacatures">
                  <Briefcase className="mr-2 h-5 w-5" />
                  Bekijk alle vacatures
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-auto py-4">
                <Link to="/">
                  <Home className="mr-2 h-5 w-5" />
                  Terug naar home
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-auto py-4">
                <Link to="/tips">
                  <Search className="mr-2 h-5 w-5" />
                  Lees onze tips
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-auto py-4">
                <Link to="/contact">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Neem contact op
                </Link>
              </Button>
            </div>

            <div className="text-sm text-muted-foreground">
              Pad: <code className="bg-muted px-2 py-1 rounded">{location.pathname}</code>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default NotFound;
